import type { ComputedRef, Ref } from "vue";
import { DSBaseListOption } from "@/src/design_system/form_elements/option_list/types";
import { getOptionId } from "@/src/design_system/form_elements/option_list/utils/optionListUtils";
import { scrollActiveOptionIntoView } from "@/src/design_system/form_elements/shared/utils/keyboardNavigation";

interface UseSelectArrowNavigationArgs {
  flatOptions: ComputedRef<DSBaseListOption[]>;
  listId: string;
  activeOptionId: Ref<string | undefined>;
  isOpen: Ref<boolean>;
  openPopover: () => void;
}

interface UseSelectArrowNavigation {
  onArrowDown: (e: KeyboardEvent) => void;
  onArrowUp: (e: KeyboardEvent) => void;
}

const wrapIndex = (index: number, length: number): number => ((index % length) + length) % length;

export const useSelectArrowNavigation = ({
  flatOptions,
  listId,
  activeOptionId,
  isOpen,
  openPopover,
}: UseSelectArrowNavigationArgs): UseSelectArrowNavigation => {
  const getCurrentIndex = (options: DSBaseListOption[]): number => {
    if (!activeOptionId.value) return -1;

    return options.findIndex(option => getOptionId(listId, option) === activeOptionId.value);
  };

  const getNextOptionId = (step: 1 | -1): string | undefined => {
    const options = flatOptions.value;
    if (options.length === 0) return undefined;

    const currentIndex = getCurrentIndex(options);
    const startIndex = step === 1 ? -1 : 0;
    const baseIndex = currentIndex < 0 ? startIndex : currentIndex;
    const nextIndex = wrapIndex(baseIndex + step, options.length);

    return getOptionId(listId, options[nextIndex]);
  };

  const onArrow = (step: 1 | -1, e: KeyboardEvent) => {
    e.preventDefault();

    if (!isOpen.value) {
      openPopover();
    }

    activeOptionId.value = getNextOptionId(step);
    scrollActiveOptionIntoView(activeOptionId.value);
  };

  const onArrowDown = (e: KeyboardEvent) => onArrow(1, e);
  const onArrowUp = (e: KeyboardEvent) => onArrow(-1, e);

  return { onArrowDown, onArrowUp };
};
